import React, { useState, useEffect } from 'react';
import { publicAPI } from '../lib/api';

const METHODOLOGY = "Every résumé is scored by a deterministic engine, then measured against a fixed set of labelled résumé–job pairs covering skill-alias equivalence, ranking order, run-to-run consistency and skill-taxonomy coverage. The figure shown is our current pass rate on that benchmark — a controlled, repeatable test, not a guarantee of individual hiring outcomes. We re-run it on every change to the scoring engine.";

/**
 * Slim, public trust badge showing the matching engine's internal benchmark
 * result, with a "How we measure this" methodology tooltip.
 * Renders nothing until data loads (graceful on failure).
 * variant: 'light' (for dark backgrounds) | 'dark' (for light backgrounds)
 */
export function BenchmarkTrustBadge({ variant = 'dark', style = {} }) {
  const [bm, setBm] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const showTip = hovered || pinned;

  useEffect(() => {
    let alive = true;
    publicAPI.matchingBenchmark()
      .then((res) => { if (alive && res.data && res.data.skill_alias_accuracy != null) setBm(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!bm) return null;

  const acc = Math.round((bm.skill_alias_accuracy ?? 0) * 100);
  const cases = (bm.cases?.skill_pairs || 0) + (bm.cases?.taxonomy_pairs || 0) + (bm.cases?.coverage_cases || 0);
  const light = variant === 'light';

  const palette = light
    ? { bg: 'rgba(255,255,255,0.12)', border: 'rgba(255,255,255,0.25)', accent: '#5EEAD4', text: 'rgba(255,255,255,0.92)', sub: 'rgba(255,255,255,0.6)', link: '#99F6E4' }
    : { bg: '#F0FDFA', border: '#99F6E4', accent: '#0D9488', text: '#0F766E', sub: '#0D9488', link: '#0D9488' };

  return (
    <div style={{ position: 'relative', display: 'inline-block', ...style }}>
      <div
        data-testid="public-benchmark-badge"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
          background: palette.bg, border: `1px solid ${palette.border}`,
          borderRadius: 999, padding: '0.5rem 1rem',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={palette.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
        </svg>
        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: palette.text }}>
          <span style={{ color: palette.accent }}>{acc}%</span> on our internal matching benchmark
        </span>
        {cases > 0 && (
          <span style={{ fontSize: '0.6875rem', color: palette.sub, borderLeft: `1px solid ${palette.border}`, paddingLeft: '0.6rem' }}>
            {cases} labelled cases · deterministic
          </span>
        )}
        <button
          type="button"
          data-testid="benchmark-methodology-trigger"
          aria-label="How we measure this"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setHovered(true)}
          onBlur={() => setHovered(false)}
          onClick={() => setPinned((p) => !p)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            background: 'none', border: 'none', cursor: 'help', padding: 0,
            fontSize: '0.6875rem', fontWeight: 600, color: palette.link,
            borderLeft: `1px solid ${palette.border}`, paddingLeft: '0.6rem',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
          </svg>
          How we measure this
        </button>
      </div>

      {showTip && (
        <div
          data-testid="benchmark-methodology-tooltip"
          role="tooltip"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
            width: 340, maxWidth: '90vw', zIndex: 60, textAlign: 'left',
            background: '#fff', color: '#334155', border: '1px solid #ECE7DE', borderRadius: 12,
            boxShadow: '0 12px 32px rgba(15,23,42,0.16)', padding: '0.875rem 1rem',
          }}
        >
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0F766E', marginBottom: '0.4rem' }}>How we measure this</div>
          <p style={{ fontSize: '0.75rem', lineHeight: 1.55, margin: 0 }}>{METHODOLOGY}</p>
          <a
            href="/ai-transparency"
            style={{ display: 'inline-block', marginTop: '0.6rem', fontSize: '0.6875rem', fontWeight: 700, color: '#0D9488', textDecoration: 'none' }}
          >
            How AI is used &rarr;
          </a>
        </div>
      )}
    </div>
  );
}
